import {
  Match,
  MatchRequest,
  MatchParticipation,
  CreateMatchDto,
  UpdateMatchDto,
  MatchFilters,
  ContactMatchDto,
} from "../types/match.types";

const BASE_URL = "/api/matches";

export const matchService = {
  getAll: (filters: MatchFilters) => {
    const query = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) query.append(key, String(value));
    });

    return `${BASE_URL}?${query.toString()}`;
  },

  create: async (data: CreateMatchDto, token: string): Promise<Match> => {
    const response = await fetch(`${import.meta.env.VITE_API_URL}${BASE_URL}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  update: async (
    id: string,
    data: UpdateMatchDto,
    token: string,
  ): Promise<Match> => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${id}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  delete: async (id: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${id}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  contact: async (id: string, data: ContactMatchDto, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${id}/contact`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  getRequests: async (token: string): Promise<{ requests: MatchRequest[] }> => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/requests`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  getParticipations: async (
    token: string,
  ): Promise<{ participations: MatchParticipation[] }> => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/participations`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  updateRequestStatus: async (
    matchId: string,
    userId: string,
    status: "accepted" | "refused",
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${matchId}/requests/${userId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  cancelRequest: async (matchId: string, userId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${matchId}/requests/${userId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },

  markNotificationsAsRead: async (matchId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${matchId}/notifications/read`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) throw new Error("Failed to mark notification as read");

    return response.json();
  },

  generatePairings: async (matchId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${matchId}/pairings/generate`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(errorText);
    }

    return response.json();
  },

  getPairings: async (matchId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${matchId}/pairings`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) throw new Error("Failed to fetch pairings");

    return response.json();
  },

  updatePairingTime: async (
    pairingId: string,
    scheduledTime: string,
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/pairings/${pairingId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ scheduled_time: scheduledTime }),
      },
    );

    if (!response.ok) throw new Error("Failed to update pairing");

    return response.json();
  },

  closeRegistrations: async (id: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}${BASE_URL}/${id}/close-registrations`,
      {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      try {
        const errorJson = JSON.parse(errorText);

        throw new Error(errorJson.error || errorJson.message || errorText);
      } catch {
        throw new Error(errorText);
      }
    }

    return response.json();
  },
};
